import React, { useState } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from '../hooks/useAuth';

interface RatingModalProps {
  isOpen: boolean;
  tutorId: string;
  onClose: () => void;
  onSuccess: () => void;
  showToast: (message: string, type?: 'success' | 'error') => void;
}

const ratingDescriptions = [
  "Très mauvais - À éviter",
  "Mauvais - Besoin d'amélioration",
  "Moyen - Correct",
  "Bon - Satisfaisant",
  "Excellent - Très satisfait"
];

export const RatingModal: React.FC<RatingModalProps> = ({ 
  isOpen, 
  tutorId, 
  onClose, 
  onSuccess,
  showToast 
}) => {
  const { user } = useAuth();
  const [selectedRating, setSelectedRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      showToast("Vous devez être connecté pour évaluer", 'error');
      return;
    }
    
    if (selectedRating === 0) {
      showToast("Veuillez sélectionner une note", 'error');
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Utiliser directement l'ID utilisateur comme tu le souhaites
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Non authentifié');
      
      // Récupérer l'ID du profil de l'utilisateur pour la table notes
      const { data: userProfile, error: profileError } = await supabase
        .from('profils')
        .select('id')
        .eq('user_id', user.id)
        .single();
        
      if (profileError || !userProfile) {
        throw new Error('Impossible de récupérer votre profil');
      }
      
      // Utiliser ton code d'upsert directement
      const { error } = await supabase
        .from('notes')
        .upsert(
          { 
            auteur_id: userProfile.id, // ID du profil (pas user.id)
            target_id: tutorId, 
            note: selectedRating 
          },
          { onConflict: 'auteur_id,target_id' }
        )
        .select()
        .single();
        
      if (error) throw error;
      
      showToast("Merci pour votre évaluation !");
      handleClose();
      onSuccess();
    } catch (error: any) {
      showToast(error.message || "Erreur lors de l'envoi de l'évaluation", 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedRating(0);
    setHoveredRating(0);
    onClose();
  };

  if (!isOpen) return null;

  const displayRating = hoveredRating || selectedRating;

  return (
    <div 
      className="modal-overlay flex items-center justify-center"
      onClick={handleClose}
    >
      <div 
        className="bg-white rounded-xl p-6 w-full max-w-md modal-content mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-gray-900 font-heading">Votre note</h3>
          <button 
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-gray-700 font-medium mb-3">Note *</label>
            <div className="flex justify-center space-x-3 mb-3">
              {[1, 2, 3, 4, 5].map(rating => (
                <i
                  key={rating}
                  className={`fas fa-star text-3xl cursor-pointer rating-star ${
                    rating <= displayRating ? 'text-yellow-400' : 'text-gray-300'
                  }`}
                  onClick={() => setSelectedRating(rating)}
                  onMouseEnter={() => setHoveredRating(rating)}
                  onMouseLeave={() => setHoveredRating(0)}
                ></i>
              ))}
            </div>
            <p className="text-sm text-gray-500 text-center">
              {displayRating > 0 
                ? ratingDescriptions[displayRating - 1] 
                : "Sélectionnez une note de 1 à 5 étoiles"}
            </p>
          </div>
          
          <button 
            type="submit" 
            disabled={submitting}
            className="w-full bg-heg-blue text-white py-3 px-6 rounded-full font-medium hover:bg-heg-blue-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="fas fa-paper-plane mr-2"></i> 
            {submitting ? 'Envoi en cours...' : "Valider la note"}
          </button>
        </form>
      </div>
    </div>
  );
}; 